import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChannelSettlementStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, enumOptions, SelectFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_SETTLEMENT_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { formatStatusLabel } from "@/lib/utils";
import { listChannelSettlements } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { type ChannelSettlementListItem, type ListChannelSettlementsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide channel Settlements list —
// same list-page shape as _app.channels.tsx. `channelId` (a supported
// backend filter) is deliberately not exposed here for the same reason
// _app.channel-payments.tsx omits it — the channel detail page's own
// Settlements sub-table already covers that case directly.
type ChannelSettlementFilters = {
  status?: ChannelSettlementStatus;
  merchantId?: string;
};

const DEFAULT_FILTERS: ChannelSettlementFilters = {};

function validateChannelSettlementsSearch(
  search: Record<string, unknown>,
): TableSearch<ChannelSettlementFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelSettlementStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
  };
}

export const Route = createFileRoute("/_app/channel-settlements")({
  validateSearch: validateChannelSettlementsSearch,
  component: ChannelSettlementsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function RowLink({ channelSettlementId, children }: { channelSettlementId: string; children: ReactNode }) {
  return (
    <Link
      to="/channel-settlements/$channelSettlementId"
      params={{ channelSettlementId }}
      className="group -m-2 block p-2"
    >
      {children}
    </Link>
  );
}

function ChannelSettlementsPage() {
  const tableState = useTableState<ChannelSettlementFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelSettlementsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelSettlementsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel-settlements", params],
    queryFn: () => listChannelSettlements(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelSettlementListItem>[] = [
    {
      key: "publicId",
      header: "Settlement ID",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <span className="font-mono group-hover:underline">{s.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (s) => (
        <MerchantCell merchantId={s.merchant.id} businessName={s.merchant.businessName} publicId={s.merchant.publicId} />
      ),
    },
    {
      key: "channel",
      header: "Channel",
      cell: (s) => (
        <Link to="/channels/$channelId" params={{ channelId: s.channel.id }} className="group -m-2 block p-2">
          <span className="block group-hover:underline">{s.channel.label}</span>
          <span className="block text-xs text-muted-foreground">{s.channel.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(ChannelSettlementStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as ChannelSettlementStatus | undefined)}
        />
      )),
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <StatusBadge label={formatStatusLabel(s.status)} color={CHANNEL_SETTLEMENT_STATUS_COLOR[s.status]} />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <AssetLabel symbol={s.asset.symbol} network={s.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "grossAmount",
      header: "Total Amount",
      sortable: true,
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          <Amount native={s.grossAmount} symbol={s.asset.symbol} />
        </RowLink>
      ),
    },
    {
      key: "paymentCount",
      header: "Payments",
      cell: (s) => <RowLink channelSettlementId={s.id}>{s.paymentCount}</RowLink>,
    },
    {
      key: "declines",
      header: "Declines (sweep/gas)",
      cell: (s) => (
        <RowLink channelSettlementId={s.id}>
          {s.sweepDeclineCount} / {s.gasFundDeclineCount}
        </RowLink>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (s) => <RowLink channelSettlementId={s.id}>{formatDate(s.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load settlements." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(s) => s.id}
        emptyMessage="No settlements match your filters."
      />
    </div>
  );
}
